import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Palette, Code, Globe, Eye, Plus, Trash2, Check, AlertCircle, Edit, X, Zap } from 'lucide-react';
import ThemeEditor from '../components/ThemeEditor';

interface UpgradePrompt {
  show: boolean;
  currentPlan: string;
  limit: number;
}

interface Theme {
  id: string;
  name: string;
  version: number;
  is_active: boolean;
  config_json: any;
  created_at: string;
}

interface Domain {
  id: string;
  host: string;
  is_active: boolean;
  created_at: string;
}

const API_URL = (import.meta as any).env?.VITE_API_URL || 'https://app.tahc.ai';

export default function WidgetTheming() {
  const { t } = useTranslation();
  const [themes, setThemes] = useState<Theme[]>([]);
  const [domains, setDomains] = useState<Domain[]>([]);
  const [activeTab, setActiveTab] = useState<'themes' | 'domains' | 'embed'>('themes');
  const [loading, setLoading] = useState(true);
  const [newDomain, setNewDomain] = useState('');
  const [extractUrl, setExtractUrl] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [showThemeEditor, setShowThemeEditor] = useState(false);
  const [editingTheme, setEditingTheme] = useState<Theme | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [upgradePrompt, setUpgradePrompt] = useState<UpgradePrompt>({ show: false, currentPlan: '', limit: 0 });

  const getAuthHeaders = (): HeadersInit => {
    const tokens = localStorage.getItem('auth_tokens');
    if (!tokens) {
      return {
        'Content-Type': 'application/json',
      };
    }
    const { accessToken } = JSON.parse(tokens);
    return {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    };
  };

  const fetchWithTokenRefresh = async (url: string, options: RequestInit = {}): Promise<Response> => {
    const headers = getAuthHeaders();
    let response = await fetch(url, {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    });

    // If we get a 401, try to refresh the token
    if (response.status === 401) {
      const tokens = localStorage.getItem('auth_tokens');
      if (tokens) {
        try {
          const { refreshToken } = JSON.parse(tokens);
          const refreshResponse = await fetch(`${API_URL}/api/auth/refresh`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ refreshToken }),
          });

          if (refreshResponse.ok) {
            const newTokens = await refreshResponse.json();
            localStorage.setItem('auth_tokens', JSON.stringify(newTokens));

            // Retry the original request with the new token
            const newHeaders = {
              ...headers,
              'Authorization': `Bearer ${newTokens.accessToken}`,
            };
            response = await fetch(url, {
              ...options,
              headers: {
                ...newHeaders,
                ...options.headers,
              },
            });
          } else {
            // Refresh failed, redirect to login
            localStorage.removeItem('auth_tokens');
            localStorage.removeItem('user');
            window.location.href = '/login';
          }
        } catch (error) {
          console.error('Token refresh failed:', error);
          localStorage.removeItem('auth_tokens');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
      }
    }

    return response;
  };

  useEffect(() => {
    loadThemes();
    loadDomains();
  }, []);

  const loadThemes = async () => {
    try {
      const response = await fetchWithTokenRefresh(`${API_URL}/api/widget/themes`);
      if (response.ok) {
        const data = await response.json();
        setThemes(data);
      }
    } catch (error) {
      console.error('Failed to load themes:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDomains = async () => {
    try {
      const response = await fetchWithTokenRefresh(`${API_URL}/api/widget/domains`);
      if (response.ok) {
        const data = await response.json();
        setDomains(data);
      }
    } catch (error) {
      console.error('Failed to load domains:', error);
    }
  };

  const activateTheme = async (themeId: string) => {
    try {
      const response = await fetchWithTokenRefresh(`${API_URL}/api/widget/themes/${themeId}`, {
        method: 'PATCH',
        body: JSON.stringify({ is_active: true }),
      });
      if (response.ok) {
        loadThemes();
      }
    } catch (error) {
      console.error('Failed to activate theme:', error);
    }
  };

  const deleteTheme = async (themeId: string) => {
    if (!confirm(t('widget.deleteThemeConfirm'))) return;

    try {
      const response = await fetchWithTokenRefresh(`${API_URL}/api/widget/themes/${themeId}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        loadThemes();
      }
    } catch (error) {
      console.error('Failed to delete theme:', error);
    }
  };

  const addDomain = async () => {
    if (!newDomain.trim()) return;

    try {
      const response = await fetchWithTokenRefresh(`${API_URL}/api/widget/domains`, {
        method: 'POST',
        body: JSON.stringify({ host: newDomain }),
      });
      if (response.ok) {
        setNewDomain('');
        loadDomains();
      } else {
        const data = await response.json();
        if (data.error === 'domain_limit_reached' && data.upgradeRequired) {
          setUpgradePrompt({
            show: true,
            currentPlan: data.currentPlan,
            limit: data.limit,
          });
        } else {
          alert(data.message || data.error || t('widget.addDomainError'));
        }
      }
    } catch (error) {
      console.error('Failed to add domain:', error);
      alert(t('widget.addDomainError'));
    }
  };

  const handleUpgrade = () => {
    // Navigate to billing settings
    window.location.href = '/dashboard?tab=settings&settingsTab=billing';
  };

  const removeDomain = async (domainId: string) => {
    if (!confirm(t('widget.removeDomainConfirm'))) return;

    try {
      const response = await fetchWithTokenRefresh(`${API_URL}/api/widget/domains/${domainId}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        loadDomains();
      }
    } catch (error) {
      console.error('Failed to remove domain:', error);
    }
  };

  const extractBrand = async () => {
    if (!extractUrl.trim()) return;

    setExtracting(true);
    try {
      // Normalize URL - add https:// if not present
      let normalizedUrl = extractUrl.trim();
      if (!normalizedUrl.match(/^https?:\/\//i)) {
        normalizedUrl = `https://${normalizedUrl}`;
      }

      const response = await fetchWithTokenRefresh(`${API_URL}/api/widget/brand/extract`, {
        method: 'POST',
        body: JSON.stringify({ url: normalizedUrl }),
      });
      if (response.ok) {
        const data = await response.json();

        console.log('Brand extraction response:', data);
        console.log('Extracted primary color:', data.suggestedTheme?.appearance?.colors?.primary);

        // Extract domain name from URL to use as theme name
        const domain = new URL(normalizedUrl).hostname.replace('www.', '');
        const themeName = `${domain} Theme`;

        // Open theme editor with extracted theme configuration
        setEditingTheme({
          id: '',
          name: themeName,
          version: 0,
          is_active: false,
          config_json: data.suggestedTheme,
          created_at: new Date().toISOString(),
        });
        console.log('Setting editing theme:', {
          name: themeName,
          primaryColor: data.suggestedTheme?.appearance?.colors?.primary
        });
        setShowThemeEditor(true);
        setExtractUrl(''); // Clear the URL input
      } else {
        const error = await response.json();
        alert(error.error || t('widget.extractBrandFailed'));
      }
    } catch (error) {
      console.error('Failed to extract brand:', error);
      alert(t('widget.extractBrandError'));
    } finally {
      setExtracting(false);
    }
  };

  const handleCreateTheme = async (name: string, config: any) => {
    try {
      const response = await fetchWithTokenRefresh(`${API_URL}/api/widget/themes`, {
        method: 'POST',
        body: JSON.stringify({ name, config }),
      });
      if (response.ok) {
        await loadThemes();
        setShowThemeEditor(false);
        setEditingTheme(null);
      } else {
        const error = await response.json();
        alert(error.error || t('widget.createThemeFailed'));
      }
    } catch (error) {
      console.error('Failed to create theme:', error);
      throw error;
    }
  };

  const handleUpdateTheme = async (name: string, config: any) => {
    if (!editingTheme || !editingTheme.id) return;

    try {
      const response = await fetchWithTokenRefresh(`${API_URL}/api/widget/themes/${editingTheme.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ name, config }),
      });
      if (response.ok) {
        await loadThemes();
        setShowThemeEditor(false);
        setEditingTheme(null);
      } else {
        const error = await response.json();
        alert(error.error || t('widget.updateThemeFailed'));
      }
    } catch (error) {
      console.error('Failed to update theme:', error);
      throw error;
    }
  };

  const handleSaveTheme = async (name: string, config: any) => {
    // If editingTheme has an ID, it's an existing theme - update it
    // Otherwise, create a new theme
    if (editingTheme?.id) {
      await handleUpdateTheme(name, config);
    } else {
      await handleCreateTheme(name, config);
    }
  };

  const handleEditTheme = (theme: Theme) => {
    setEditingTheme(theme);
    setShowThemeEditor(true);
  };

  const handleCloseEditor = () => {
    setShowThemeEditor(false);
    setEditingTheme(null);
  };

  // Fetch organization subdomain for embed code
  const [subdomain, setSubdomain] = useState('');
  const [subdomainLoading, setSubdomainLoading] = useState(true);

  useEffect(() => {
    // Fetch organization subdomain
    const fetchSubdomain = async () => {
      try {
        const response = await fetchWithTokenRefresh(`${API_URL}/api/auth/me`);
        if (response.ok) {
          const data = await response.json();
          console.log('Auth me response:', data);
          if (data.organization?.subdomain) {
            setSubdomain(data.organization.subdomain);
          } else {
            console.warn('No subdomain in response, using fallback');
          }
        } else {
          console.error('Failed to fetch /auth/me:', response.status);
        }
      } catch (error) {
        console.error('Failed to fetch subdomain:', error);
      } finally {
        setSubdomainLoading(false);
      }
    };
    fetchSubdomain();
  }, []);

  const embedCode = `<script src="${API_URL}/widget.js" data-workspace-id="${subdomain}"></script>`;

  return (
    <>
      <ThemeEditor
        isOpen={showThemeEditor}
        onClose={handleCloseEditor}
        onSave={handleSaveTheme}
        initialConfig={editingTheme?.config_json}
        initialName={editingTheme?.name}
        isEditing={!!editingTheme?.id}
      />

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full h-full max-w-7xl max-h-[95vh] flex flex-col">
            {/* Modal Header */}
            <div className="p-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <h3 className="text-lg font-semibold">{t('widget.widgetPreview')}</h3>
              <button
                onClick={() => setShowPreview(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {/* Modal Content - iframe */}
            <div className="flex-1 overflow-hidden">
              <iframe
                key={Date.now()}
                src={`${API_URL}/demo.html?subdomain=${subdomain}&t=${Date.now()}`}
                className="w-full h-full border-0"
                title="Widget Preview"
              />
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Prompt Modal */}
      {upgradePrompt.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                <Zap className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{t('widget.domainLimitReached')}</h3>
                <p className="text-sm text-gray-500">
                  {t('widget.currentPlan')}: <span className="font-medium capitalize">{upgradePrompt.currentPlan}</span>
                </p>
              </div>
            </div>
            <p className="text-gray-600 mb-6">
              {t('widget.domainLimitMessage', {
                limit: upgradePrompt.limit,
                unit: upgradePrompt.limit === 1 ? t('widget.domain') : t('widget.domains')
              })}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setUpgradePrompt({ show: false, currentPlan: '', limit: 0 })}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleUpgrade}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition flex items-center justify-center gap-2"
              >
                <Zap className="w-4 h-4" />
                {t('widget.upgradePlan')}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Palette className="w-6 h-6 text-gray-900" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{t('widget.title')}</h1>
              <p className="text-sm text-gray-500 mt-1">
                {t('widget.subtitle')}
              </p>
            </div>
          </div>
          <button
            className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition"
            onClick={() => setShowThemeEditor(true)}
          >
            <Plus className="w-4 h-4" />
            <span>{t('widget.createTheme')}</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 px-6">
        <div className="flex gap-6">
          <button
            onClick={() => setActiveTab('themes')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition ${
              activeTab === 'themes'
                ? 'border-black text-black'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center gap-2">
              <Palette className="w-4 h-4" />
              <span>{t('widget.themes')}</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('domains')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition ${
              activeTab === 'domains'
                ? 'border-black text-black'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4" />
              <span>{t('widget.allowedDomains')}</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('embed')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition ${
              activeTab === 'embed'
                ? 'border-black text-black'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center gap-2">
              <Code className="w-4 h-4" />
              <span>{t('widget.embedCode')}</span>
            </div>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {activeTab === 'themes' && (
          <div className="space-y-6">
            {/* Brand Extractor */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold mb-4">{t('widget.quickStartTitle')}</h3>
              <p className="text-sm text-gray-600 mb-4">
                {t('widget.quickStartDescription')}
              </p>
              <div className="flex gap-3">
                <input
                  type="text"
                  placeholder={t('widget.urlPlaceholder')}
                  value={extractUrl}
                  onChange={(e) => setExtractUrl(e.target.value)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                />
                <button
                  onClick={extractBrand}
                  disabled={extracting}
                  className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition disabled:opacity-50"
                >
                  {extracting ? t('widget.extracting') : t('widget.extract')}
                </button>
              </div>
            </div>

            {/* Themes List */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">{t('widget.yourThemes')}</h3>
                <button
                  onClick={() => {
                    const activeTheme = themes.find(t => t.is_active);
                    if (activeTheme) {
                      setShowPreview(true);
                    } else {
                      alert(t('widget.noActiveTheme'));
                    }
                  }}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition"
                >
                  <Eye className="w-4 h-4" />
                  <span>{t('widget.previewWidget')}</span>
                </button>
              </div>
              {loading ? (
                <div className="text-center py-8 text-gray-500">{t('widget.loadingThemes')}</div>
              ) : themes.length === 0 ? (
                <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                  <Palette className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600 mb-2">{t('widget.noThemes')}</p>
                  <p className="text-sm text-gray-500">{t('widget.createFirstTheme')}</p>
                </div>
              ) : (
                <div className="grid gap-3">
                  {themes.map((theme) => (
                    <div
                      key={theme.id}
                      className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <h4 className="font-semibold text-gray-900">{theme.name}</h4>
                            {theme.is_active && (
                              <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full flex items-center gap-1">
                                <Check className="w-3 h-3" />
                                {t('common.active')}
                              </span>
                            )}
                            <span className="text-sm text-gray-500">v{theme.version}</span>
                          </div>
                          <div className="flex items-center gap-4 mt-2">
                            <div className="flex items-center gap-2">
                              <div
                                className="w-6 h-6 rounded border border-gray-300"
                                style={{ backgroundColor: theme.config_json?.appearance?.colors?.primary || '#000' }}
                              />
                              <span className="text-xs text-gray-500">
                                {theme.config_json?.appearance?.colors?.primary || '#000'}
                              </span>
                            </div>
                            <span className="text-xs text-gray-400">
                              {new Date(theme.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {!theme.is_active && (
                            <button
                              onClick={() => activateTheme(theme.id)}
                              className="px-4 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 transition"
                            >
                              {t('widget.activate')}
                            </button>
                          )}
                          <button
                            onClick={() => handleEditTheme(theme)}
                            className="p-2 text-gray-500 hover:text-gray-700 transition"
                            title={t('widget.editTheme')}
                          >
                            <Edit className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => setShowPreview(true)}
                            className="p-2 text-gray-500 hover:text-gray-700 transition"
                            title={t('widget.previewWidget')}
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                          {!theme.is_active && (
                            <button
                              onClick={() => deleteTheme(theme.id)}
                              className="p-2 text-red-500 hover:text-red-700 transition"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'domains' && (
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">{t('widget.securityTitle')}</p>
                <p>{t('widget.securityDescription')}</p>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold mb-4">{t('widget.addAllowedDomain')}</h3>
              <div className="flex gap-3">
                <input
                  type="text"
                  placeholder={t('widget.domainPlaceholder')}
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addDomain()}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                />
                <button
                  onClick={addDomain}
                  className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition"
                >
                  {t('common.add')}
                </button>
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="text-lg font-semibold">{t('widget.allowedDomains')}</h3>
              {domains.length === 0 ? (
                <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                  <Globe className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600 mb-2">{t('widget.noDomainsConfigured')}</p>
                  <p className="text-sm text-gray-500">{t('widget.addDomainsToEnable')}</p>
                </div>
              ) : (
                <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-200">
                  {domains.map((domain) => (
                    <div key={domain.id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                      <div className="flex items-center gap-3">
                        <Globe className="w-5 h-5 text-gray-400" />
                        <div>
                          <p className="font-medium text-gray-900">{domain.host}</p>
                          <p className="text-xs text-gray-500">
                            {t('widget.added')} {new Date(domain.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => removeDomain(domain.id)}
                        className="p-2 text-red-500 hover:text-red-700 transition"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'embed' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold mb-4">{t('widget.widgetEmbedCode')}</h3>
              <p className="text-sm text-gray-600 mb-4">
                {t('widget.embedCodeInstructions')}
              </p>
              {subdomainLoading ? (
                <div className="bg-gray-100 p-4 rounded-lg text-center text-gray-500">
                  Loading embed code...
                </div>
              ) : !subdomain ? (
                <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg text-yellow-700 text-sm">
                  Unable to load your workspace ID. Please refresh the page.
                </div>
              ) : (
                <div className="relative">
                  <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
                    <code>{embedCode}</code>
                  </pre>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(embedCode);
                      alert(t('widget.copiedToClipboard'));
                    }}
                    className="absolute top-4 right-4 px-3 py-1 bg-gray-700 text-white text-xs rounded hover:bg-gray-600 transition"
                  >
                    {t('common.copy')}
                  </button>
                </div>
              )}
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold mb-4">{t('widget.testYourWidget')}</h3>
              <p className="text-sm text-gray-600 mb-4">
                {t('widget.testWidgetDescription')}
              </p>
              <a
                href={subdomain ? `${API_URL}/demo.html?subdomain=${subdomain}` : '#'}
                target="_blank"
                className={`inline-flex items-center gap-2 px-6 py-2 rounded-lg transition ${
                  subdomain
                    ? 'bg-black text-white hover:bg-gray-800'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                onClick={(e) => !subdomain && e.preventDefault()}
              >
                <Eye className="w-4 h-4" />
                <span>{t('widget.openDemo')}</span>
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
}
